import type { SavedDiagnosisState } from '../types/diagnosisState.types';

/**
 * Get the state number (1-indexed) for a given state ID based on chronological order.
 * The original state is always first, followed by saved states sorted by created_at.
 *
 * @param stateId - The state ID to find, or null for the original state
 * @param savedStates - Array of all saved diagnosis states
 * @returns The 1-indexed state number, or null if not found
 */
export function getStateNumber(
  stateId: string | null,
  savedStates: SavedDiagnosisState[]
): number | null {
  if (!savedStates || savedStates.length === 0) {
    return null;
  }

  // Separate original state from saved states
  const originalState = savedStates.find(
    s => s.id === 'original' || s.isOriginal
  );
  const savedStatesOnly = savedStates.filter(
    s => s.id !== 'original' && !s.isOriginal
  );

  // Sort saved states by created_at to ensure chronological order
  const sortedSavedStates = [...savedStatesOnly].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return dateA - dateB;
  });

  // Reconstruct the full ordered array: original first, then sorted saved states
  const orderedStates = originalState
    ? [originalState, ...sortedSavedStates]
    : sortedSavedStates;

  // Find the index of the state
  let stateIndex: number;
  if (!stateId) {
    // Original state (id === 'original' or isOriginal === true)
    stateIndex = orderedStates.findIndex(
      s => s.id === 'original' || s.isOriginal
    );
  } else {
    stateIndex = orderedStates.findIndex(s => s.id === stateId);
  }

  // Return state number (1-indexed) or null if not found
  return stateIndex >= 0 ? stateIndex + 1 : null;
}
